import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import {
  Users, BookOpen, Calendar, Megaphone, GraduationCap,
  CreditCard, Clock, TrendingUp, ArrowRight, RefreshCw,
  CheckCircle, AlertCircle, Library, Bell, Award,
} from 'lucide-react';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export default function LMSDashboard() {
  const { account, institution } = useLMSAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    if (!institution?.id) return;
    setLoading(true);
    const id   = institution.id;
    const today = new Date().toISOString().split('T')[0];
    const dow   = new Date().getDay();

    try {
      const [
        studentsRes, classesRes, teachersRes,
        assignmentsRes, attendanceTodayRes,
        feesRes, feesPaidRes, feesOverdueRes,
        booksRes, overdueborrowsRes,
        examsRes, eventsRes,
        announcementsRes, noticesRes,
        timetableRes, recentStudentsRes,
        recentAttRes,
      ] = await Promise.all([
        // Students
        supabase.from('profiles').select('id',{count:'exact',head:true})
          .eq('institution_id',id).eq('role','student'),
        // Classes
        supabase.from('lms_classes').select('id,name,total_students,class_teacher_name',{count:'exact'})
          .eq('institution_id',id).neq('is_active',false),
        // Teachers
        supabase.from('lms_teachers').select('id',{count:'exact',head:true})
          .eq('institution_id',id).eq('status','active'),
        // Assignments due this week
        supabase.from('lms_assignments').select('id,title,subject,due_date',{count:'exact'})
          .eq('institution_id',id).eq('is_published',true)
          .gte('due_date', today)
          .order('due_date').limit(5),
        // Attendance marked today
        supabase.from('lms_attendance_sessions').select('id,class_id,present_count,absent_count,total_students,marked_by_name')
          .eq('institution_id',id).eq('session_date',today),
        // Fee vouchers pending
        supabase.from('lms_fee_vouchers').select('id',{count:'exact',head:true})
          .eq('institution_id',id).eq('status','pending'),
        // Fee vouchers paid
        supabase.from('lms_fee_vouchers').select('id',{count:'exact',head:true})
          .eq('institution_id',id).eq('status','paid'),
        // Fee vouchers overdue
        supabase.from('lms_fee_vouchers').select('id',{count:'exact',head:true})
          .eq('institution_id',id).eq('status','overdue'),
        // Library books
        supabase.from('lms_books').select('id,available_copies,total_copies',{count:'exact'})
          .eq('institution_id',id),
        // Overdue borrows
        supabase.from('lms_borrow_records').select('id',{count:'exact',head:true})
          .eq('institution_id',id).eq('status','overdue'),
        // Upcoming exams
        supabase.from('lms_exam_datesheets').select('id,subject,exam_date,start_time')
          .eq('institution_id',id)
          .gte('exam_date', today)
          .order('exam_date').limit(5),
        // Upcoming events
        supabase.from('lms_events').select('id,title,event_date,category')
          .eq('institution_id',id).eq('is_published',true).eq('is_cancelled',false)
          .gte('event_date', today)
          .order('event_date').limit(4),
        // Recent announcements
        supabase.from('lms_announcements').select('id,title,priority,created_at')
          .eq('institution_id',id).eq('is_published',true)
          .order('created_at',{ascending:false}).limit(4),
        // Recent notices
        supabase.from('lms_notices').select('id,title,notice_type,created_at')
          .eq('institution_id',id).eq('is_published',true)
          .order('created_at',{ascending:false}).limit(3),
        // Today's timetable
        supabase.from('lms_timetable').select('id,subject,start_time,end_time,room,teacher_name,class_id')
          .eq('institution_id',id).eq('day_of_week',dow).eq('is_active',true)
          .order('start_time').limit(8),
        // Recent students
        supabase.from('profiles').select('id,full_name,username,degree,year_of_study,roll_no,created_at')
          .eq('institution_id',id).eq('role','student')
          .order('created_at',{ascending:false}).limit(6),
        // Recent attendance records
        supabase.from('lms_attendance_sessions')
          .select('id,session_date,present_count,absent_count,total_students,marked_by_name,class_id')
          .eq('institution_id',id)
          .order('session_date',{ascending:false}).limit(5),
      ]);

      // Fee stats
      const feeStats = await supabase.rpc('get_fee_stats',{ p_institution_id: id })
        .then(r => r.data).catch(()=>null);

      setData({
        students:        studentsRes.count || 0,
        classes:         classesRes.data   || [],
        classCount:      classesRes.count  || 0,
        teachers:        teachersRes.count || 0,
        assignments:     assignmentsRes.data || [],
        assignmentCount: assignmentsRes.count || 0,
        attendanceToday: attendanceTodayRes.data || [],
        feesPending:     feesRes.count     || 0,
        feesPaid:        feesPaidRes.count || 0,
        feesOverdue:     feesOverdueRes.count || 0,
        feeStats,
        books:           booksRes.data     || [],
        bookCount:       booksRes.count    || 0,
        overdueBooks:    overdueborrowsRes.count || 0,
        exams:           examsRes.data     || [],
        events:          eventsRes.data    || [],
        announcements:   announcementsRes.data || [],
        notices:         noticesRes.data   || [],
        timetable:       timetableRes.data || [],
        recentStudents:  recentStudentsRes.data || [],
        recentAtt:       recentAttRes.data || [],
        today,
        dayName:         DAYS[dow],
      });
    } catch(e) { console.error('Dashboard load error:', e); }
    finally { setLoading(false); }
  }, [institution?.id, refresh]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <LMSLayout>
      <LMSPageGuard pageKey="dashboard">
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'60vh',gap:16}}>
          <div style={{width:48,height:48,border:'4px solid #E5E7EB',borderTopColor:'#4F8EF7',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
          <div style={{color:'#9CA3AF',fontSize:14}}>Loading dashboard...</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </LMSPageGuard>
    </LMSLayout>
  );

  if (!data) return null;

  const totalBooksOut    = data.books.reduce((a,b)=>a+(b.total_copies-b.available_copies),0);
  const todayAttClasses  = data.attendanceToday.length;
  const totalAttStudents = data.attendanceToday.reduce((a,s)=>a+s.present_count,0);
  const paidAmount       = Number(data.feeStats?.total_collected||0);
  const pendingAmount    = Number(data.feeStats?.total_pending||0);

  // Colors
  const C = {
    blue:'#3B6EE8', purple:'#7C3AED', green:'#059669',
    orange:'#F59E0B', red:'#EF4444', teal:'#0891B2',
    pink:'#DB2777', indigo:'#4F46E5',
  };

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="dashboard">

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:24,fontWeight:900,color:'#111827',margin:0}}>
              {institution?.name || 'LMS Dashboard'}
            </h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>
              {data.dayName}, {new Date(data.today+'T12:00:00').toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}
            </p>
          </div>
          <button onClick={()=>setRefresh(r=>r+1)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>
            <RefreshCw size={14}/> Refresh
          </button>
        </div>

        {/* ── MAIN STATS ROW ── */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14,marginBottom:24}}>
          {[
            { label:'Total Students',  value:data.students,    icon:Users,          color:C.blue,   link:'/lms/students',     sub:`across ${data.classCount} classes`     },
            { label:'Active Classes',  value:data.classCount,  icon:BookOpen,       color:C.purple, link:'/lms/classes',      sub:`${data.classes.reduce((a,c)=>a+(c.total_students||0),0)} enrolled` },
            { label:'Teachers',        value:data.teachers,    icon:GraduationCap,  color:C.green,  link:'/lms/teachers',     sub:'active staff'                           },
            { label:'Assignments Due', value:data.assignmentCount, icon:Award,      color:C.orange, link:'/lms/assignments',  sub:'this week'                              },
            { label:'Books Available', value:data.books.reduce((a,b)=>a+b.available_copies,0), icon:Library, color:C.teal, link:'/lms/library', sub:`${totalBooksOut} checked out` },
            { label:'Fees Pending',    value:data.feesPending, icon:CreditCard,     color:data.feesPending>0?C.red:C.green, link:'/lms/fees', sub:`${data.feesOverdue} overdue` },
          ].map(s=>{
            const Icon = s.icon;
            return (
              <Link key={s.label} to={s.link} style={{textDecoration:'none'}}>
                <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',padding:'18px 16px',cursor:'pointer',transition:'box-shadow 0.15s',boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.05)'}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                    <div style={{width:40,height:40,borderRadius:12,background:`${s.color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <Icon size={20} color={s.color}/>
                    </div>
                    <ArrowRight size={14} color="#D1D5DB"/>
                  </div>
                  <div style={{fontSize:28,fontWeight:900,color:s.color,lineHeight:1}}>{s.value}</div>
                  <div style={{fontSize:13,fontWeight:700,color:'#111827',marginTop:4}}>{s.label}</div>
                  <div style={{fontSize:11,color:'#9CA3AF',marginTop:2}}>{s.sub}</div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* ── TODAY'S ATTENDANCE ── */}
        <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20,marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:36,height:36,borderRadius:10,background:`${C.green}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <CheckCircle size={18} color={C.green}/>
              </div>
              <div>
                <div style={{fontWeight:800,color:'#111827',fontSize:15}}>Today's Attendance</div>
                <div style={{fontSize:12,color:'#9CA3AF'}}>{data.dayName} · {todayAttClasses} class{todayAttClasses!==1?'es':''} marked</div>
              </div>
            </div>
            <Link to="/lms/attendance" style={{fontSize:12,fontWeight:700,color:C.blue,background:'#EFF6FF',padding:'5px 12px',borderRadius:6,textDecoration:'none',display:'flex',alignItems:'center',gap:4}}>
              Mark Attendance <ArrowRight size={12}/>
            </Link>
          </div>
          {data.attendanceToday.length===0 ? (
            <div style={{textAlign:'center',padding:'24px 16px',background:'#FFF7ED',borderRadius:12,border:'1px dashed #FED7AA'}}>
              <div style={{fontSize:28,marginBottom:8}}>📋</div>
              <div style={{fontWeight:700,color:'#92400E',fontSize:14}}>No attendance marked yet today</div>
              <div style={{fontSize:12,color:'#B45309',marginTop:4}}>Class teachers can mark attendance from the Attendance page</div>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
              {data.attendanceToday.map(s=>{
                const cls = data.classes.find(c=>c.id===s.class_id);
                const pct = s.total_students>0?Math.round(s.present_count/s.total_students*100):0;
                return (
                  <div key={s.id} style={{background:'#F8FAFF',borderRadius:12,border:'1px solid #E2E8F0',padding:'12px 14px'}}>
                    <div style={{fontWeight:700,color:'#111827',fontSize:13,marginBottom:6}}>{cls?.name||'Class'}</div>
                    <div style={{display:'flex',gap:10,fontSize:12,marginBottom:8}}>
                      <span style={{color:C.green,fontWeight:700}}>✅ {s.present_count}</span>
                      <span style={{color:C.red,fontWeight:700}}>❌ {s.absent_count}</span>
                      <span style={{color:'#9CA3AF'}}>/ {s.total_students}</span>
                    </div>
                    <div style={{background:'#E5E7EB',borderRadius:20,height:6,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:pct>=75?C.green:C.red,borderRadius:20}}/>
                    </div>
                    <div style={{fontSize:10,color:'#9CA3AF',marginTop:4}}>{pct}% attendance · {s.marked_by_name||'—'}</div>
                  </div>
                );
              })}
              {/* Unmarked classes */}
              {data.classes.filter(c=>!data.attendanceToday.find(a=>a.class_id===c.id)).slice(0,4).map(c=>(
                <div key={c.id} style={{background:'#FFF7ED',borderRadius:12,border:'1px dashed #FED7AA',padding:'12px 14px'}}>
                  <div style={{fontWeight:700,color:'#92400E',fontSize:13,marginBottom:4}}>{c.name}</div>
                  <div style={{fontSize:11,color:'#B45309'}}>⏳ Not marked yet</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── 3-COLUMN MIDDLE ROW ── */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:20}}>

          {/* Today's Timetable */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <Clock size={16} color={C.blue}/> Today's Classes
              </div>
              <Link to="/lms/timetable" style={{fontSize:11,color:C.blue,textDecoration:'none',fontWeight:700}}>See All</Link>
            </div>
            {data.timetable.length===0 ? (
              <div style={{textAlign:'center',padding:24,color:'#9CA3AF',fontSize:13}}>No classes scheduled today</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {data.timetable.slice(0,6).map((t,i)=>{
                  const colors=[C.blue,C.purple,C.green,C.orange,C.teal,C.pink];
                  const c=colors[i%colors.length];
                  return (
                    <div key={t.id} style={{display:'flex',gap:10,alignItems:'center',padding:'8px 10px',borderRadius:10,background:`${c}08`,border:`1px solid ${c}20`}}>
                      <div style={{width:4,height:36,borderRadius:4,background:c,flexShrink:0}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:700,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.subject}</div>
                        <div style={{fontSize:10,color:'#9CA3AF'}}>{t.start_time}–{t.end_time}{t.room?` · Room ${t.room}`:''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Fee Summary */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <CreditCard size={16} color={C.orange}/> Fee Summary
              </div>
              <Link to="/lms/fees" style={{fontSize:11,color:C.orange,textDecoration:'none',fontWeight:700}}>Manage</Link>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {[
                {label:'Collected',  value:`Rs ${paidAmount.toLocaleString()}`,    color:C.green,  bg:'#F0FDF4', emoji:'✅'},
                {label:'Pending',    value:`Rs ${pendingAmount.toLocaleString()}`,  color:C.orange, bg:'#FFF7ED', emoji:'⏳'},
                {label:'Overdue',    value:`${data.feesOverdue} vouchers`,          color:C.red,    bg:'#FEF2F2', emoji:'⚠️'},
                {label:'Paid',       value:`${data.feesPaid} vouchers`,             color:C.green,  bg:'#F0FDF4', emoji:'🟢'},
              ].map(s=>(
                <div key={s.label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 12px',borderRadius:10,background:s.bg}}>
                  <span style={{fontSize:12,color:'#374151'}}>{s.emoji} {s.label}</span>
                  <span style={{fontSize:13,fontWeight:800,color:s.color}}>{s.value}</span>
                </div>
              ))}
            </div>
            {data.feesOverdue>0 && (
              <div style={{marginTop:10,fontSize:12,color:C.red,fontWeight:600,textAlign:'center',background:'#FEF2F2',padding:'8px',borderRadius:8}}>
                ⚠️ {data.feesOverdue} students have overdue fees
              </div>
            )}
          </div>

          {/* Library Status */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <BookOpen size={16} color={C.teal}/> Library
              </div>
              <Link to="/lms/library" style={{fontSize:11,color:C.teal,textDecoration:'none',fontWeight:700}}>Manage</Link>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {[
                {label:'Total Books',    value:data.bookCount,                                              color:C.teal,   emoji:'📚'},
                {label:'Available',      value:data.books.reduce((a,b)=>a+b.available_copies,0),           color:C.green,  emoji:'✅'},
                {label:'Checked Out',    value:totalBooksOut,                                               color:C.blue,   emoji:'📖'},
                {label:'Overdue',        value:data.overdueBooks,                                           color:data.overdueBooks>0?C.red:'#9CA3AF', emoji:'⚠️'},
              ].map(s=>(
                <div key={s.label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 12px',borderRadius:10,background:'#F8FAFF'}}>
                  <span style={{fontSize:12,color:'#374151'}}>{s.emoji} {s.label}</span>
                  <span style={{fontSize:14,fontWeight:900,color:s.color}}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── BOTTOM ROW ── */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>

          {/* Assignments Due */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <Award size={16} color={C.orange}/> Assignments Due Soon
              </div>
              <Link to="/lms/assignments" style={{fontSize:11,color:C.orange,textDecoration:'none',fontWeight:700}}>See All</Link>
            </div>
            {data.assignments.length===0 ? (
              <div style={{textAlign:'center',padding:24,color:'#9CA3AF',fontSize:13}}>🎉 No assignments due this week</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {data.assignments.map(a=>{
                  const due = new Date(a.due_date);
                  const diff= Math.ceil((due-new Date())/86400000);
                  const urgent = diff<=2;
                  return (
                    <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:urgent?'#FEF2F2':'#F8FAFF',border:`1px solid ${urgent?'#FECACA':'#E2E8F0'}`}}>
                      <span style={{fontSize:18}}>{urgent?'🔥':'📋'}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.title}</div>
                        <div style={{fontSize:11,color:urgent?C.red:'#9CA3AF'}}>{a.subject}</div>
                      </div>
                      <div style={{fontSize:11,fontWeight:800,color:urgent?C.red:C.orange,flexShrink:0}}>
                        {diff===0?'Today!':diff===1?'Tomorrow':`${diff}d`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Upcoming Exams */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <TrendingUp size={16} color={C.red}/> Upcoming Exams
              </div>
              <Link to="/lms/exams" style={{fontSize:11,color:C.red,textDecoration:'none',fontWeight:700}}>See All</Link>
            </div>
            {data.exams.length===0 ? (
              <div style={{textAlign:'center',padding:24,color:'#9CA3AF',fontSize:13}}>No upcoming exams</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {data.exams.map(e=>{
                  const diff=Math.ceil((new Date(e.exam_date)-new Date())/86400000);
                  return (
                    <div key={e.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:'#FEF2F2',border:'1px solid #FECACA'}}>
                      <span style={{fontSize:18}}>📝</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.subject}</div>
                        <div style={{fontSize:11,color:'#9CA3AF'}}>{e.exam_date} · {e.start_time}</div>
                      </div>
                      <div style={{fontSize:11,fontWeight:800,color:diff<=3?C.red:C.orange,flexShrink:0}}>
                        {diff===0?'Today!':diff===1?'Tomorrow':`${diff}d`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── ANNOUNCEMENTS + EVENTS ROW ── */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>

          {/* Announcements */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <Megaphone size={16} color={C.indigo}/> Announcements
              </div>
              <Link to="/lms/feed" style={{fontSize:11,color:C.indigo,textDecoration:'none',fontWeight:700}}>See All</Link>
            </div>
            {data.announcements.length===0 ? (
              <div style={{textAlign:'center',padding:24,color:'#9CA3AF',fontSize:13}}>No announcements yet</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {data.announcements.map(a=>{
                  const pc={'normal':C.blue,'important':C.orange,'urgent':C.red}[a.priority]||C.blue;
                  return (
                    <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:'#F8FAFF',borderLeft:`3px solid ${pc}`}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.title}</div>
                        <div style={{fontSize:11,color:'#9CA3AF'}}>{new Date(a.created_at).toLocaleDateString()}</div>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,color:pc,background:`${pc}15`,padding:'2px 8px',borderRadius:4,flexShrink:0}}>{a.priority||'normal'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Events */}
          <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                <Calendar size={16} color={C.pink}/> Upcoming Events
              </div>
              <Link to="/lms/events" style={{fontSize:11,color:C.pink,textDecoration:'none',fontWeight:700}}>See All</Link>
            </div>
            {data.events.length===0 ? (
              <div style={{textAlign:'center',padding:24,color:'#9CA3AF',fontSize:13}}>No upcoming events</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {data.events.map((e,i)=>{
                  const colors=[C.teal,C.pink,C.purple,C.blue];
                  const c=colors[i%colors.length];
                  const d=new Date(e.event_date);
                  return (
                    <div key={e.id} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 12px',borderRadius:10,background:'#F8FAFF',border:`1px solid ${c}20`}}>
                      <div style={{width:38,height:38,borderRadius:10,background:`${c}15`,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <div style={{fontSize:13,fontWeight:900,color:c,lineHeight:1}}>{d.getDate()}</div>
                        <div style={{fontSize:9,color:c,fontWeight:700}}>{d.toLocaleString('default',{month:'short'})}</div>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.title}</div>
                        <div style={{fontSize:11,color:'#9CA3AF'}}>{e.category||'Event'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── RECENT STUDENTS ── */}
        <div style={{background:'#fff',borderRadius:18,border:'1px solid #E5E7EB',padding:20,marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
            <div style={{fontWeight:800,color:'#111827',fontSize:15,display:'flex',alignItems:'center',gap:8}}>
              <Users size={16} color={C.blue}/> Recently Joined Students
            </div>
            <Link to="/lms/students" style={{fontSize:11,color:C.blue,textDecoration:'none',fontWeight:700,display:'flex',alignItems:'center',gap:4}}>
              View All <ArrowRight size={11}/>
            </Link>
          </div>
          {data.recentStudents.length===0 ? (
            <div style={{textAlign:'center',padding:24,color:'#9CA3AF',fontSize:13}}>No students yet</div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
              {data.recentStudents.map((s,i)=>{
                const colors=[C.blue,C.purple,C.green,C.orange,C.teal,C.pink];
                const c=colors[i%colors.length];
                return (
                  <div key={s.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:12,background:'#F8FAFF',border:'1px solid #E2E8F0'}}>
                    <div style={{width:36,height:36,borderRadius:'50%',background:`linear-gradient(135deg,${c},${c}99)`,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:14,fontWeight:800,flexShrink:0}}>
                      {(s.full_name||'?')[0]}
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.full_name||'—'}</div>
                      {s.roll_no && <div style={{fontSize:10,color:C.blue,fontWeight:600}}>Roll# {s.roll_no}</div>}
                      <div style={{fontSize:10,color:'#9CA3AF'}}>{s.degree||''}{s.year_of_study?` · Yr${s.year_of_study}`:''}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── QUICK LINKS ── */}
        <div style={{background:'linear-gradient(135deg,#1E293B,#0F172A)',borderRadius:18,padding:20}}>
          <div style={{fontWeight:800,color:'#fff',fontSize:15,marginBottom:14}}>⚡ Quick Actions</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10}}>
            {[
              {label:'Mark Attendance', link:'/lms/attendance', emoji:'📋', color:C.green},
              {label:'Add Student',     link:'/lms/students',   emoji:'👤', color:C.blue},
              {label:'New Assignment',  link:'/lms/assignments',emoji:'📝', color:C.purple},
              {label:'Generate Fee',    link:'/lms/fees',       emoji:'💰', color:C.orange},
              {label:'Issue Book',      link:'/lms/library',    emoji:'📖', color:C.teal},
              {label:'Post Notice',     link:'/lms/notices',    emoji:'📌', color:C.pink},
              {label:'Send Notification',link:'/lms/notifications',emoji:'🔔',color:C.indigo},
              {label:'View Reports',    link:'/lms/reports',    emoji:'📊', color:C.red},
            ].map(q=>(
              <Link key={q.label} to={q.link} style={{textDecoration:'none'}}>
                <div style={{background:'rgba(255,255,255,0.07)',borderRadius:12,padding:'12px 14px',border:`1px solid ${q.color}30`,cursor:'pointer',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.currentTarget.style.background=`${q.color}20`}
                  onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.07)'}>
                  <div style={{fontSize:22,marginBottom:6}}>{q.emoji}</div>
                  <div style={{fontSize:12,fontWeight:700,color:'#fff'}}>{q.label}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

      </LMSPageGuard>
    </LMSLayout>
  );
}